import { Box, useMedia } from '@opendoor/bricks-next';
import type { useQuery } from 'platform/apollo';
import { Button } from 'platform/bricks-next';
import { signOutOfAllDevices } from 'platform/components/AuthContext';
import type { CommsPreferencesQuery } from 'platform/graphql/__generated__/useCommsPreferences';
import type { CustomerDetailsQuery } from 'platform/graphql/__generated__/useCustomerDetails';
import { useToast } from 'shared/hooks/useToast';

import { BasicInfoContainer } from './BasicInfoContainer';
import { CommsPreferencesContainer } from './CommsPreferencesContainer';
import { ContactInfoContainer } from './ContactInfoContainer';
import { PasswordUpdateContainer } from './PasswordUpdateContainer';

export const genericErrorText = 'Something went wrong please try again later';

export type TextInputSize = 'medium' | 'large' | 'small';

/**
 * Account Preferences Component that allows user to update their name, phone number and other preferences
 */
export const AccountPreferences = ({
  customerDetails,
  commsPreferences,
  refetchCustomerDetails,
}: {
  customerDetails?: CustomerDetailsQuery;
  commsPreferences?: CommsPreferencesQuery;
  refetchCustomerDetails: ReturnType<typeof useQuery>['refetch'];
}) => {
  const { largerThanSM } = useMedia();
  const { showToast, ToastElement } = useToast();

  const inputSize = largerThanSM ? 'medium' : 'small';

  const customerInfo = customerDetails?.customerService?.getCurrentCustomer?.customer;

  const commsSubscriptions =
    commsPreferences?.customerCommPreferences.getCommPreference?.commPreference?.subscriptions;

  return (
    <Box
      maxWidth={960}
      mx="auto"
      paddingHorizontal="$16x"
      paddingVertical="$32x"
      gap="$32x"
      $largerThanSM={{
        gap: '$44x',
      }}
    >
      <BasicInfoContainer customerInfo={customerInfo} inputSize={inputSize} showToast={showToast} />
      <ContactInfoContainer
        customerInfo={customerInfo}
        inputSize={inputSize}
        refetch={refetchCustomerDetails}
        showToast={showToast}
      />
      <PasswordUpdateContainer />
      <CommsPreferencesContainer
        subscriptions={commsSubscriptions}
        inputSize={inputSize}
        customerInfo={customerInfo}
        showToast={showToast}
      />
      <Box alignItems="flex-end">
        <Button
          id="sign-out-button"
          label="Sign out of all devices"
          variant="secondary"
          size={inputSize}
          $largerThanSM={{
            maxWidth: 262,
          }}
          analyticsName="cake-update-account-settings"
          onPress={() => void signOutOfAllDevices()}
        />
      </Box>
      {ToastElement}
    </Box>
  );
};
